import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ThumbsUp, ThumbsDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TrainingExampleDialog from "@/components/agent/TrainingExampleDialog";
import { AGENT_TRAINING } from "@/constants/testIds";

const SELECT_CLASS = "h-10 rounded-md border border-input bg-background px-3 text-sm";
const SOURCE_LABELS = { agent: "Respuesta del agente", staff: "Respuesta del equipo", manual: "Escrito a mano" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "");

// Fix AO (2026-10-23): ejemplos con los que se entrena al agente (calificaciones del equipo y ejemplos escritos a mano).
export default function TrainingTab({ config, onSave }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState("");
  const [source, setSource] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { toast } = useToast();
  const enabled = config?.training_enabled !== false;

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/agent/training/items", { params: { limit: 200, ...(rating ? { rating } : {}), ...(source ? { source } : {}) } });
      setData(d); setError("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron cargar los ejemplos");
    }
  }, [rating, source]);
  useEffect(() => { load(); }, [load]);

  const fail = (title, err) => toast({ title, description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });

  const save = async (values) => {
    try {
      if (editing) await api.put(`/agent/training/items/${editing.id}`, values);
      else await api.post("/agent/training/items", values);
      toast({ title: editing ? "Ejemplo guardado" : "Ejemplo agregado" });
      setFormOpen(false); setEditing(null); load();
      return true;
    } catch (err) {
      fail("No se pudo guardar", err);
      return false;
    }
  };

  const toggle = async (item, active) => {
    try {
      await api.put(`/agent/training/items/${item.id}`, { active });
      load();
    } catch (err) {
      fail("No se pudo cambiar el ejemplo", err);
    }
  };

  const remove = async () => {
    const target = deleting; setDeleting(null);
    try {
      await api.delete(`/agent/training/items/${target.id}`);
      toast({ title: "Ejemplo borrado" }); load();
    } catch (err) {
      fail("No se pudo borrar", err);
    }
  };

  const counts = data?.counts;
  return (
    <div className="max-w-4xl space-y-6" data-testid={AGENT_TRAINING.tab}>
      <div className="bg-white border border-border rounded-xl p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-foreground">Usar los ejemplos para responder</p>
            <p className="text-sm text-muted-foreground">En cada consulta, el agente recibe hasta 6 buenas respuestas y 4 correcciones, las más parecidas a lo que escribe el lead. Los precios y la información siguen saliendo siempre de la base de conocimiento.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={(v) => onSave({ training_enabled: v })} data-testid={AGENT_TRAINING.enabledToggle} />
        </div>
        <p className="text-sm text-muted-foreground">Calificá las respuestas del agente en Conversaciones con el pulgar hacia arriba o hacia abajo (con una nota de qué estuvo mal), o marcá una respuesta del equipo como ejemplo. También podés escribir ejemplos acá.</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground" data-testid={AGENT_TRAINING.counts}>
          {counts ? `${counts.up} ${counts.up === 1 ? "buena respuesta" : "buenas respuestas"} · ${counts.down} ${counts.down === 1 ? "corrección" : "correcciones"} · ${counts.active} ${counts.active === 1 ? "activo" : "activos"}` : ""}
        </p>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-brand hover:bg-brand-hover text-white gap-1.5" data-testid={AGENT_TRAINING.newButton}>
          <Plus className="w-4 h-4" /> Nuevo ejemplo
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select className={SELECT_CLASS} value={rating} onChange={(e) => setRating(e.target.value)} aria-label="Filtrar por tipo" data-testid={AGENT_TRAINING.ratingFilter}>
          <option value="">Todos los tipos</option>
          <option value="up">Buenas respuestas</option>
          <option value="down">A evitar</option>
        </select>
        <select className={SELECT_CLASS} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Filtrar por origen" data-testid={AGENT_TRAINING.sourceFilter}>
          <option value="">Todos los orígenes</option>
          <option value="agent">Respuestas del agente</option>
          <option value="staff">Respuestas del equipo</option>
          <option value="manual">Escritos a mano</option>
        </select>
      </div>

      {error && <p className="text-sm text-destructive break-words">{error}</p>}
      {data === null && !error && <p className="text-sm text-muted-foreground">Cargando...</p>}
      {data && data.items.length === 0 && <p className="text-sm text-muted-foreground" data-testid={AGENT_TRAINING.empty}>{counts.total === 0 ? "Todavía no hay ejemplos. Calificá una respuesta del agente en Conversaciones o escribí uno con \"Nuevo ejemplo\"." : "No hay ejemplos con ese filtro."}</p>}
      <div className="space-y-3">
        {(data?.items || []).map((i) => {
          const good = i.rating === "up";
          const unused = !good && !i.comment && !i.correction;
          return (
            <div key={i.id} className={`bg-white border border-border rounded-xl p-4 ${i.active ? "" : "opacity-60"}`} data-testid={`${AGENT_TRAINING.rowPrefix}${i.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${good ? "bg-[#E3EDE1] text-[#5B7A54]" : "bg-[#F6DEDC] text-[#A4473C]"} border-none font-normal text-[11px] gap-1`}>
                    {good ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}{good ? "Buena respuesta" : "A evitar"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{SOURCE_LABELS[i.source] || i.source}{i.created_by_name ? ` · ${i.created_by_name}` : ""} · {fmtDate(i.created_at)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Switch checked={i.active} onCheckedChange={(v) => toggle(i, v)} aria-label="Usar este ejemplo" data-testid={`${AGENT_TRAINING.activeTogglePrefix}${i.id}`} />
                  <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setEditing(i); setFormOpen(true); }} data-testid={`${AGENT_TRAINING.editPrefix}${i.id}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                  <Button variant="ghost" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => setDeleting(i)} data-testid={`${AGENT_TRAINING.deletePrefix}${i.id}`}><Trash2 className="w-3.5 h-3.5" /> Borrar</Button>
                </div>
              </div>
              <p className="text-sm mt-2 break-words"><span className="text-muted-foreground">Lead: </span>{i.lead_message || "—"}</p>
              <p className="text-sm mt-1 whitespace-pre-wrap break-words"><span className="text-muted-foreground">{good ? "Respuesta: " : "Respuesta que no sirvió: "}</span>{i.reply}</p>
              {i.comment && <p className="text-sm mt-1 break-words"><span className="text-muted-foreground">{good ? "Por qué sirve: " : "Motivo: "}</span>{i.comment}</p>}
              {i.correction && <p className="text-sm mt-1 whitespace-pre-wrap break-words"><span className="text-muted-foreground">Mejor respuesta: </span>{i.correction}</p>}
              {unused && <p className="text-xs text-[#B5793A] mt-2">Sin motivo ni mejor respuesta: no cambia lo que responde el agente. Editalo para agregarlos.</p>}
              {!i.active && <p className="text-xs text-muted-foreground mt-2">Apagado: el agente no lo usa.</p>}
            </div>
          );
        })}
      </div>

      <TrainingExampleDialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }} item={editing} onSubmit={save} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar este ejemplo?</AlertDialogTitle>
            <AlertDialogDescription>El agente deja de usarlo y no se puede deshacer. Si solo querés que no lo use por ahora, apagalo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid={AGENT_TRAINING.deleteConfirm}>Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
